import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import AceleresMessageApi from '../../../api/aceleres/messageActions';
import { debounce } from '@chatwoot/utils';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import {
  createPendingMessage,
  mergeLocalMediaPreviews,
} from 'dashboard/helper/commons';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import {
  buildConversationList,
  buildConversationPrefetchPlan,
  getConversationDetailsPrefetchLimit,
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
  isOnFoldersView,
  getPrefetchConversationLimit,
  normalizeConversationPrefetchLimit,
} from './helpers/actionHelpers';
import { restoreConversationPrefetchSnapshot } from 'dashboard/helper/conversationPrefetchCache';
import { conversationSessionCache } from 'dashboard/helper/conversationSessionCache';
import {
  sanitizeConversationListUnreadForViewer,
  sanitizeConversationUnreadForViewer,
  sanitizeMessageConversationUnreadForViewer,
  shouldPreserveAssignedUnreadForBox,
} from 'dashboard/helper/unreadOwnership';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import { useAlert } from 'dashboard/composables';

const DEFAULT_MESSAGE_PREFETCH_LIMIT = 20;
const DEFAULT_DETAILS_PREFETCH_LIMIT = 100;
const inflightConversationRequests = new Map();
const inflightFullHistoryRequests = new Map();
const hydratedHistoryConversationIds = new Set();
let conversationListRequestSequence = 0;
// Aceleres: numero de fetches de lista em voo. O spinner so pode ser desligado
// quando chega a zero. Antes o loading era limpo apenas se o requestId ainda
// fosse o atual, e emptyAllConversations/replaceAllConversations/
// replaceConversationPage incrementam a sequence SEM disparar novo fetch — se
// isso acontecesse durante um GET que falhava, o CLEAR era descartado e o
// spinner girava para sempre ate o usuario dar F5.
let conversationListActiveRequests = 0;

const beginConversationListRequest = commit => {
  conversationListActiveRequests += 1;
  commit(types.SET_LIST_LOADING_STATUS);
  commit(types.CLEAR_LIST_ERROR_STATUS);
};

const finishConversationListRequest = commit => {
  conversationListActiveRequests = Math.max(
    0,
    conversationListActiveRequests - 1
  );
  if (conversationListActiveRequests === 0) {
    commit(types.CLEAR_LIST_LOADING_STATUS);
  }
};

const normalizeConversationListRequestParams = params => {
  if (params?.assigneeType !== 'personal') return params;

  return { ...params, assigneeType: 'all' };
};

const buildConversationListRequestKey = params =>
  JSON.stringify(
    Object.entries(params || {})
      .filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
  );
const FULL_HISTORY_BATCH_LIMIT = 100;
const HYDRATED_HISTORY_CACHE_LIMIT = 200;
const MESSAGE_PRELOAD_BATCH_SIZE = 100;

const rememberHydratedHistory = conversationId => {
  hydratedHistoryConversationIds.delete(conversationId);
  hydratedHistoryConversationIds.add(conversationId);

  if (hydratedHistoryConversationIds.size > HYDRATED_HISTORY_CACHE_LIMIT) {
    const oldestConversationId = hydratedHistoryConversationIds
      .values()
      .next().value;
    hydratedHistoryConversationIds.delete(oldestConversationId);
  }
};

const getMessageSortKey = message => {
  const createdAt = Number(message?.created_at);
  if (Number.isFinite(createdAt)) return createdAt;

  const messageId = Number(message?.id);
  return Number.isFinite(messageId) ? messageId : 0;
};

const mergeConversationMessages = (currentMessages = [], newMessages = []) => {
  const messagesById = new Map();

  [...currentMessages, ...newMessages].forEach(message => {
    if (message?.id) messagesById.set(message.id, message);
  });

  return [...messagesById.values()].sort((left, right) => {
    const difference = getMessageSortKey(left) - getMessageSortKey(right);
    return difference || Number(left?.id || 0) - Number(right?.id || 0);
  });
};

const preloadConversationListMessages = async (
  conversations,
  messageLimit = DEFAULT_MESSAGE_PREFETCH_LIMIT,
  throwOnError = false
) => {
  const targets = conversations
    .filter(conversation => conversation?.id)
    .map(conversation => {
      const target = { conversation_id: Number(conversation.id) };
      const beforeMessageId = conversation.messages?.[0]?.id;
      if (beforeMessageId) target.before_message_id = beforeMessageId;
      return target;
    });

  if (!targets.length) return conversations;

  try {
    const batches = Array.from(
      { length: Math.ceil(targets.length / MESSAGE_PRELOAD_BATCH_SIZE) },
      (_, index) =>
        targets.slice(
          index * MESSAGE_PRELOAD_BATCH_SIZE,
          (index + 1) * MESSAGE_PRELOAD_BATCH_SIZE
        )
    );
    const responses = await Promise.all(
      batches.map(batch =>
        MessageApi.preloadMessages({
          conversations: batch,
          limit: batch.length,
          messageLimit,
        })
      )
    );
    const payload = responses.flatMap(response => response.data?.payload || []);
    const messagesByConversationId = new Map(
      payload.map(item => [Number(item.conversation_id), item.messages || []])
    );

    return conversations.map(conversation => {
      const conversationId = Number(conversation?.id);
      if (!messagesByConversationId.has(conversationId)) return conversation;

      const prefetchedMessages = messagesByConversationId.get(conversationId);
      return {
        ...conversation,
        messages: mergeConversationMessages(
          conversation.messages || [],
          prefetchedMessages
        ),
        dataFetched: true,
        prefetchProgress: 100,
        allMessagesLoaded: prefetchedMessages.length < messageLimit,
      };
    });
  } catch (error) {
    if (throwOnError) throw error;
    // A lista continua utilizável; ao abrir, o fluxo normal recupera as mensagens.
    return conversations;
  }
};

const refreshConversationStats = (dispatch, state) => {
  return dispatch(
    'conversationStats/get',
    { ...(state?.conversationFilters || {}), immediate: true },
    { root: true }
  );
};

// Aceleres fix (2026-08-06): a preview de "última mensagem" na lista de
// conversas (last_non_activity_message) é calculada no backend com escopo
// por agente (Aceleres::ConversationHistoryScope: segmento de atendimento,
// corte por ciclo de resolução, conversas pessoais). O broadcast realtime de
// message.created, porém, não aplica esse escopo e o frontend adota
// otimisticamente "a mensagem mais nova" como preview. Isso pode deixar a
// preview mostrando uma mensagem que a API de listagem não mostraria para
// aquele agente. Para corrigir sem duplicar a regra de escopo no cliente,
// agendamos uma releitura debounced (por conversa) da conversa via
// ConversationApi.show, que já retorna o last_non_activity_message correto.
const previewReconciliationDebouncers = new Map();

const scheduleConversationPreviewReconciliation = (
  dispatch,
  conversationId
) => {
  if (!conversationId || typeof dispatch !== 'function') return;

  if (!previewReconciliationDebouncers.has(conversationId)) {
    previewReconciliationDebouncers.set(
      conversationId,
      debounce(
        () => dispatch('reconcileConversationPreview', conversationId),
        1500
      )
    );
  }

  previewReconciliationDebouncers.get(conversationId)();
};

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// actions
const actions = {
  getConversation: ({ commit, state, rootGetters }, conversationId) => {
    const forceRefresh =
      typeof conversationId === 'object' &&
      conversationId?.forceRefresh === true;
    const respectAceleresBox =
      typeof conversationId === 'object' &&
      conversationId?.respectAceleresBox === true;
    const requestedConversationId =
      typeof conversationId === 'object' ? conversationId?.id : conversationId;
    const normalizedConversationId = Number(requestedConversationId);

    if (!normalizedConversationId) {
      return Promise.resolve(null);
    }

    const pendingRequest = inflightConversationRequests.get(
      normalizedConversationId
    );
    if (pendingRequest) {
      return pendingRequest;
    }

    // Trocas de caixa substituem a lista visível, mas não devem obrigar a
    // buscar novamente uma conversa que já teve as mensagens carregadas nesta
    // sessão. O cache é atualizado pelos mesmos mutations do store e recebe
    // novas mensagens via websocket.
    const cachedConversation = [...conversationSessionCache.values()]
      .reverse()
      .find(conversation => {
        const currentAccountId = Number(rootGetters?.getCurrentAccountId || 0);
        const cachedAccountId = Number(conversation?.account_id || 0);

        return (
          Number(conversation?.id) === normalizedConversationId &&
          (!currentAccountId ||
            !cachedAccountId ||
            cachedAccountId === currentAccountId)
        );
      });
    if (
      !forceRefresh &&
      cachedConversation?.conversationDetailsFetched === true
    ) {
      commit(types.SET_ALL_CONVERSATION, [cachedConversation]);
      return Promise.resolve(cachedConversation);
    }

    const preserveAssignedUnread = shouldPreserveAssignedUnreadForBox(
      state?.conversationFilters?.aceleresBox
    );
    const request = ConversationApi.show(normalizedConversationId, {
      aceleresBox: state?.conversationFilters?.aceleresBox,
    })
      .then(response => {
        if (respectAceleresBox && response.data?.aceleres_box_match === false) {
          return null;
        }
        const conversation = {
          ...sanitizeConversationUnreadForViewer(
            response.data,
            rootGetters?.getCurrentUser?.id,
            { preserveAssignedUnread }
          ),
          conversationDetailsFetched: true,
        };
        // Uma conversa aberta diretamente pela URL pode não estar na página
        // atual da lista (por exemplo, uma conversa pessoal ou de outra
        // caixa). Ela ainda precisa entrar no store para o painel conseguir
        // selecioná-la depois da busca global.
        commit(types.SET_ALL_CONVERSATION, [conversation]);
        if (Array.isArray(conversation.messages)) {
          commit(types.SET_MISSING_MESSAGES, {
            id: conversation.id,
            data: conversation.messages,
          });
        }
        commit(types.SET_CHAT_DATA_FETCHED, conversation.id);
        commit(`contacts/${types.SET_CONTACT_ITEM}`, conversation.meta.sender);

        return conversation;
      })
      .catch(() => null)
      .finally(() => {
        if (
          inflightConversationRequests.get(normalizedConversationId) === request
        ) {
          inflightConversationRequests.delete(normalizedConversationId);
        }
      });

    inflightConversationRequests.set(normalizedConversationId, request);

    return request;
  },

  restoreConversationPrefetchCache: (
    { commit, dispatch },
    { accountId, userId } = {}
  ) => {
    const restoredSnapshot = restoreConversationPrefetchSnapshot({
      accountId,
      userId,
    });
    const conversations = restoredSnapshot?.conversations || [];

    if (!conversations.length) {
      return [];
    }

    commit(types.SET_ALL_CONVERSATION, conversations);
    dispatch('conversationLabels/setBulkConversationLabels', conversations, {
      root: true,
    });

    conversations.forEach(conversation => {
      const sender = conversation?.meta?.sender;
      if (sender?.id) {
        commit(`contacts/${types.SET_CONTACT_ITEM}`, sender);
      }
    });

    return conversations;
  },

  fetchAllConversations: async ({ commit, state, dispatch, rootGetters }) => {
    // Aceleres 2026-07-01: stale-while-revalidate (cache localStorage 5min) foi
    // REMOVIDO — servia lista/mensagens/atributos com até 5min de atraso e causava
    // a percepção de "só atualiza com F5" (mensagens/activity não apareciam sem
    // refresh). Agora busca sempre fresco na API.
    beginConversationListRequest(commit);
    // Aceleres 4 caixas: captura a caixa requisitada p/ descartar a resposta se o
    // usuario trocar de caixa durante o fetch (o pool SET_ALL_CONVERSATION acumula;
    // sem isso, resposta de uma caixa antiga polui a caixa atual ao trocar rapido).
    conversationListRequestSequence += 1;
    const requestId = conversationListRequestSequence;
    const requestedBox = state.conversationFilters.aceleresBox;
    // Aceleres: separa "a API falhou" de "falhou depois que os dados chegaram".
    // Um erro dentro de buildConversationList (ex.: um listener de label
    // quebrando) nao deve pintar de erro uma lista que ja foi renderizada.
    let responseReceived = false;
    try {
      const params = { ...state.conversationFilters };
      const originalAssigneeType = params.assigneeType;
      const apiParams = normalizeConversationListRequestParams(params);
      const requestedRequestKey = buildConversationListRequestKey(apiParams);
      const {
        data: { data },
      } = await ConversationApi.get(apiParams);
      if (
        requestId !== conversationListRequestSequence ||
        state.conversationFilters.aceleresBox !== requestedBox ||
        buildConversationListRequestKey(
          normalizeConversationListRequestParams(state.conversationFilters)
        ) !== requestedRequestKey
      ) {
        return;
      }
      responseReceived = true;
      await buildConversationList(
        { commit, state, dispatch, rootGetters },
        apiParams,
        data,
        originalAssigneeType
      );
    } catch (error) {
      // Só marca erro se esta ainda é a requisição corrente — falha de uma caixa
      // abandonada não deve pintar de erro a caixa que o usuário está vendo.
      if (!responseReceived && requestId === conversationListRequestSequence) {
        commit(types.SET_LIST_ERROR_STATUS);
      }
    } finally {
      finishConversationListRequest(commit);
    }
  },

  fetchFilteredConversations: async (
    { commit, state, dispatch, rootGetters },
    params
  ) => {
    conversationListRequestSequence += 1;
    const requestId = conversationListRequestSequence;
    const requestParams = { ...params, skipCounts: true };
    beginConversationListRequest(commit);
    let responseReceived = false;
    try {
      const { data } = await ConversationApi.filter(requestParams);
      if (requestId !== conversationListRequestSequence) {
        return;
      }
      responseReceived = true;
      await buildConversationList(
        { commit, state, dispatch, rootGetters },
        requestParams,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Aceleres: o catch era vazio e deixava listLoadingStatus=true (spinner
      // eterno / empty state nunca aparecia) quando o /conversations/filter
      // rejeitava (ex.: HTTP 422 InvalidAttribute do filtro "Atendidos pela IA"
      // em contas sem o CustomAttributeDefinition). Resetar pra false libera o
      // empty state contextual do ChatList ("Nenhum atendimento com IA encontrado").
      // 422 de filtro invalido é resposta legitima da API (lista vazia), nao falha
      // de carregamento — so 5xx/rede viram estado de erro com botao de retry.
      const status = error?.response?.status;
      if (
        !responseReceived &&
        requestId === conversationListRequestSequence &&
        (!status || status >= 500)
      ) {
        commit(types.SET_LIST_ERROR_STATUS);
      }
    } finally {
      finishConversationListRequest(commit);
    }
  },

  emptyAllConversations(
    { commit },
    { preserveSelectedConversation = false } = {}
  ) {
    conversationListRequestSequence += 1;
    commit(types.EMPTY_ALL_CONVERSATION, { preserveSelectedConversation });
  },

  // Aceleres: escudo do "Iniciar atendimento" contra payload em voo.
  // Ver pendingStartWrites em store/modules/conversations/index.js.
  markConversationAttendanceStarted(
    { commit },
    { conversationId, assignee = null, attributes = {} } = {}
  ) {
    commit(types.ACELERES_MARK_ATTENDANCE_STARTED, {
      conversationId,
      assignee,
      attributes,
    });
  },

  restoreConversationFromSessionCache({ commit }, conversationId) {
    commit(types.RESTORE_CONVERSATION_FROM_SESSION_CACHE, conversationId);
  },

  replaceAllConversations({ commit }, conversations = []) {
    conversationListRequestSequence += 1;
    commit(types.REPLACE_ALL_CONVERSATION, conversations);
  },

  replaceConversationPage({ commit, dispatch }, conversations = []) {
    conversationListRequestSequence += 1;
    commit(types.REPLACE_ALL_CONVERSATION, conversations);
    dispatch('conversationLabels/setBulkConversationLabels', conversations, {
      root: true,
    });

    conversations.forEach(conversation => {
      const sender = conversation?.meta?.sender;
      if (sender?.id) {
        commit(`contacts/${types.SET_CONTACT_ITEM}`, sender);
      }
    });
  },

  prefetchConversationList: async ({ rootGetters }, params) => {
    const {
      withMessages = false,
      messageLimit = DEFAULT_MESSAGE_PREFETCH_LIMIT,
      ...requestParams
    } = params;
    // Aceleres: prefetch é best-effort (aquece abas em segundo plano). Falhar aqui
    // nao pode virar unhandled rejection nem derrubar quem chamou. `null`
    // distingue a falha de uma pagina legitimamente vazia e permite que o
    // refresh de tempo real reagende a consulta.
    let response;
    try {
      response = await ConversationApi.get(requestParams);
    } catch (error) {
      return null;
    }
    const data = response?.data?.data;

    const conversations = sanitizeConversationListUnreadForViewer(
      data?.payload || [],
      rootGetters?.getCurrentUser?.id,
      {
        preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
          requestParams?.aceleresBox
        ),
      }
    );

    if (!withMessages) return conversations;

    return preloadConversationListMessages(conversations, messageLimit);
  },

  prefetchConversationMessages: async (
    _,
    {
      conversations = [],
      messageLimit = DEFAULT_MESSAGE_PREFETCH_LIMIT,
      strict = false,
    } = {}
  ) => preloadConversationListMessages(conversations, messageLimit, strict),

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit, rootGetters }, data) => {
    try {
      const request = data.limit
        ? window.axios.get(
            `/api/v1/accounts/${rootGetters.getCurrentAccountId}/conversations/${data.conversationId}/messages`,
            {
              params: {
                before: data.before,
                after:
                  data.after && Number(data.after) !== Number(data.before)
                    ? data.after
                    : undefined,
                limit: data.limit,
              },
            }
          )
        : MessageApi.getPreviousMessages(data);
      const {
        data: { meta, payload },
      } = await request;
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      // `can_load_previous` reflete o histórico real (via ConversationHistoryScope
      // no backend) e é a fonte de verdade quando presente. Usar `!payload.length`
      // isoladamente travava "carregar mais" para sempre sempre que uma página
      // filtrada vinha vazia (ex.: corte de segmento de atendimento), mesmo com
      // mensagens antigas visíveis existindo — sintoma de "só aparece histórico
      // recente". Só cai no fallback por tamanho de página quando a API não
      // manda essa informação.
      const hasHistoryFlag = meta?.history?.can_load_previous !== undefined;
      const allLoaded = hasHistoryFlag
        ? meta.history.can_load_previous === false
        : !payload.length;
      if (allLoaded) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }

      return { meta, payload };
    } catch (error) {
      // Handle error
      return null;
    }
  },

  prefetchRecentConversationMessages: async (
    { commit, state, rootGetters },
    data
  ) => {
    const conversations = Array.isArray(data.conversations)
      ? data.conversations
      : [];
    const requestedLimit = normalizeConversationPrefetchLimit(data.limit);
    const conversationLimit = data.ensureReady
      ? requestedLimit
      : Math.min(
          requestedLimit,
          getPrefetchConversationLimit(rootGetters?.getCurrentUser)
        );
    const messageLimit = DEFAULT_MESSAGE_PREFETCH_LIMIT;
    const excludedConversationIds = conversations
      .filter(conversation => {
        return (
          conversation &&
          conversation.id &&
          (conversation.dataFetched === true ||
            conversation.allMessagesLoaded === true ||
            Number(conversation.prefetchProgress || 0) > 0)
        );
      })
      .map(conversation => conversation.id);

    const { targets } = buildConversationPrefetchPlan({
      conversations,
      limit: conversationLimit,
      selectedChatId: data.ensureReady ? null : state.selectedChatId,
      excludedConversationIds,
    });

    if (!targets.length) {
      return;
    }

    const requestConversations = targets.map(
      ({ conversationId, beforeMessageId }) => {
        const payload = { conversation_id: conversationId };
        if (beforeMessageId) {
          payload.before_message_id = beforeMessageId;
        }
        return payload;
      }
    );

    targets.forEach(({ conversationId }) => {
      commit(types.SET_CONVERSATION_PREFETCH_PROGRESS, {
        conversationId,
        progress: 25,
      });
    });

    try {
      const {
        data: { payload = [] },
      } = await MessageApi.preloadMessages({
        conversations: requestConversations,
        limit: conversationLimit,
        messageLimit,
      });

      const returnedConversationIds = new Set();

      payload.forEach(item => {
        const conversationId = Number(item.conversation_id);
        if (Number.isNaN(conversationId)) {
          return;
        }

        returnedConversationIds.add(conversationId);

        const messages = Array.isArray(item.messages) ? item.messages : [];
        if (
          !data.ensureReady &&
          Number(state.selectedChatId) === Number(conversationId)
        ) {
          return;
        }

        if (messages.length) {
          commit(types.SET_PREVIOUS_CONVERSATIONS, {
            id: conversationId,
            data: messages,
          });
        } else {
          commit(types.SET_ALL_MESSAGES_LOADED, conversationId);
        }

        commit(types.SET_CHAT_DATA_FETCHED, conversationId);
        commit(types.SET_CONVERSATION_PREFETCH_PROGRESS, {
          conversationId,
          progress: 100,
        });
      });

      targets.forEach(({ conversationId }) => {
        if (returnedConversationIds.has(Number(conversationId))) {
          return;
        }

        commit(types.SET_CONVERSATION_PREFETCH_PROGRESS, {
          conversationId,
          progress: 0,
        });
      });
    } catch (error) {
      targets.forEach(({ conversationId }) => {
        commit(types.SET_CONVERSATION_PREFETCH_PROGRESS, {
          conversationId,
          progress: 0,
        });
      });
      // Ignore preload failures; opening the conversation still falls back to the normal path.
    }
  },

  mergeConversationPage({ commit, dispatch }, conversations = []) {
    commit(types.SET_ALL_CONVERSATION, conversations);
    dispatch('conversationLabels/setBulkConversationLabels', conversations, {
      root: true,
    });

    conversations.forEach(conversation => {
      const sender = conversation?.meta?.sender;
      if (sender?.id) {
        commit(`contacts/${types.SET_CONTACT_ITEM}`, sender);
      }
    });
  },

  prefetchRecentConversationDetails: async (
    { dispatch, state, rootGetters },
    data
  ) => {
    const conversations = Array.isArray(data.conversations)
      ? data.conversations
      : [];
    const requestedLimit = Number.parseInt(data.limit, 10);
    const forceRefresh = data.forceRefresh === true;
    const conversationLimit = Math.min(
      Number.isNaN(requestedLimit)
        ? DEFAULT_DETAILS_PREFETCH_LIMIT
        : requestedLimit,
      getConversationDetailsPrefetchLimit(rootGetters?.getCurrentUser)
    );

    if (conversationLimit <= 0) {
      return;
    }

    const selectedConversationId = Number(state.selectedChatId);
    const seenConversationIds = new Set();
    const targetConversationIds = [];

    for (let index = 0; index < conversations.length; index += 1) {
      const conversation = conversations[index];
      const conversationId = Number(conversation?.id);

      const shouldSkip =
        !conversationId ||
        conversationId === selectedConversationId ||
        seenConversationIds.has(conversationId) ||
        (conversation?.conversationDetailsFetched === true && !forceRefresh) ||
        inflightConversationRequests.has(conversationId);

      if (!shouldSkip) {
        seenConversationIds.add(conversationId);
        targetConversationIds.push(conversationId);

        if (targetConversationIds.length >= conversationLimit) {
          break;
        }
      }
    }

    if (!targetConversationIds.length) {
      return;
    }

    await Promise.allSettled(
      targetConversationIds.map(conversationId =>
        dispatch('getConversation', conversationId)
      )
    );
  },

  hydrateConversationHistoryFully: ({ dispatch, getters }, conversationId) => {
    const normalizedConversationId = Number(conversationId);

    if (!normalizedConversationId) {
      return Promise.resolve(null);
    }

    if (hydratedHistoryConversationIds.has(normalizedConversationId)) {
      return Promise.resolve(
        getters.getConversationById(normalizedConversationId) || null
      );
    }

    const inflightRequest = inflightFullHistoryRequests.get(
      normalizedConversationId
    );
    if (inflightRequest) {
      return inflightRequest;
    }

    const request = (async () => {
      let conversation = getters.getConversationById(normalizedConversationId);

      while (conversation?.allMessagesLoaded !== true) {
        const oldestLoadedMessageId = conversation?.messages?.[0]?.id;

        if (!oldestLoadedMessageId) break;

        const result = await dispatch('fetchPreviousMessages', {
          conversationId: normalizedConversationId,
          before: oldestLoadedMessageId,
          limit: FULL_HISTORY_BATCH_LIMIT,
        });

        if (!result) break;

        conversation = getters.getConversationById(normalizedConversationId);
      }

      rememberHydratedHistory(normalizedConversationId);
      return getters.getConversationById(normalizedConversationId) || null;
    })().finally(() => {
      if (
        inflightFullHistoryRequests.get(normalizedConversationId) === request
      ) {
        inflightFullHistoryRequests.delete(normalizedConversationId);
      }
    });

    inflightFullHistoryRequests.set(normalizedConversationId, request);

    return request;
  },

  fetchAllAttachments: async ({ commit, state }, conversationId) => {
    let attachments = [];
    let failed = false;

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      // A API busca primeiro as 100 mídias mais recentes. A inversão acontece
      // só depois da página chegar, para manter esse recorte e expor a galeria
      // em ordem cronológica; anexos em tempo real já entram no fim da lista.
      attachments = [...data.payload].reverse();
    } catch (error) {
      failed = true;
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      //
      // Aceleres 2026-08-17: mas nao quando ja ha anexos guardados. O commit
      // substitui a lista inteira, entao uma ida falhada (timeout, 500) APAGAVA
      // o historico de midia que ja estava em maos -- e esta acao corre a cada
      // abertura de conversa. A invariante que o comentario acima protege
      // continua de pe: se ainda nao existe nada para esta conversa, grava-se a
      // lista vazia como antes.
      const stored = state.attachments[conversationId] || [];
      if (!failed || !stored.length) {
        commit(types.SET_ALL_ATTACHMENTS, {
          id: conversationId,
          data: attachments,
        });
      }
    }
  },

  /**
   * Aceleres 2026-09-09: relê do servidor as mensagens cuja mídia ainda está a
   * caminho e ATUALIZA-AS no store.
   *
   * Existe porque o broadcast `message.updated` do Zoraxy é escopado ao
   * RESPONSÁVEL pela conversa (lib/aceleres/action_cable_conversation_scope.rb):
   * medido em 09/09/2026 na conversa 2569/13, o conjunto de destinatários tinha
   * um único token, o do responsável. Quem apenas acompanha — um gestor, por
   * exemplo — não recebe evento nenhum e o cartão "Entregando no atendimento"
   * ficava lá até um F5. Este caminho não mexe na regra de visibilidade: pede a
   * mesma rota REST que a conversa já usa, que reaplica os mesmos cortes.
   *
   * `syncActiveConversationMessages` não serve para isto: ele filtra por
   * `missingMessages` e só INSERE o que ainda não está no store — uma mensagem
   * já carregada nunca é atualizada por lá.
   *
   * @returns {Promise<number[]>} ids que deixaram de estar pendentes.
   */
  refreshPendingMediaMessages: async (
    { commit, state },
    { conversationId, messageIds = [] }
  ) => {
    const ids = messageIds.map(Number).filter(Number.isFinite);
    const chat = state.allConversations.find(
      conversation => Number(conversation.id) === Number(conversationId)
    );
    if (!chat || !ids.length) return [];

    try {
      const {
        data: { payload = [] },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: Math.min(...ids) - 1,
      });

      const resolvidas = [];
      payload.forEach(message => {
        // Só actualiza o que o store JÁ tem. Empurrar aqui uma mensagem nova
        // faria o ADD_MESSAGE correr os efeitos de mensagem recém-chegada
        // (contador por ler, scroll até ao fim) a meio de uma releitura de
        // fundo, que o utilizador não pediu.
        const conhecida = chat.messages.some(
          item => Number(item.id) === Number(message.id)
        );
        if (!conhecida) return;

        commit(types.ADD_MESSAGE, message);

        const attributes = message.content_attributes || {};
        const stage = String(attributes.wame_media_stage || '').toLowerCase();
        const aindaPendente =
          attributes.wame_media_pending === true ||
          ['bank', 'chatwoot'].includes(stage);
        if (ids.includes(Number(message.id)) && !aindaPendente) {
          resolvidas.push(Number(message.id));
        }
      });

      return resolvidas;
    } catch (error) {
      return [];
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch(
        'markMessagesRead',
        { id: conversationId, auto: true },
        { root: true }
      );
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit }, { data }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.SET_CHAT_DATA_FETCHED, data.id);
    commit(types.SET_CONVERSATION_PREFETCH_PROGRESS, {
      conversationId: data.id,
      progress: 100,
    });
    if (data?.meta?.history?.can_load_previous === false) {
      commit(types.SET_ALL_MESSAGES_LOADED, data.id);
    }
  },

  previewActiveChat({ commit }, { data }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
  },

  assignAgent: async (
    { dispatch, state },
    { conversationId, agentId, personal = false, start = false }
  ) => {
    const response = await ConversationApi.assignAgent({
      conversationId,
      agentId,
      personal,
      start,
    });
    if (response.data?.type === 'pending_transfer_request') {
      if (typeof window !== 'undefined') {
        Reflect.set(
          window,
          '__aceleresPendingTransferToast',
          response.data?.target_assignee?.name || true
        );
      }
      return response.data;
    }

    dispatch('setCurrentChatAssignee', {
      conversationId,
      assignee: response.data,
    });
    await refreshConversationStats(dispatch, state);
    return response.data;
  },

  moveConversationToMine: async (
    { dispatch, rootGetters, state },
    { conversationId }
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    if (!currentUser?.id) return null;

    await ConversationApi.assignAgent({
      conversationId,
      agentId: currentUser.id,
      start: true,
    });
    // `forceRefresh` e obrigatorio aqui: `getConversation` tem cache de sessao
    // e devolve o retrato guardado sempre que a conversa ja foi lida nesta aba
    // -- o que acontece tanto quando o vendedor abre a conversa antes de move-la
    // quanto pelo `prefetchRecentConversationDetails`, que carrega as recentes
    // da caixa sozinho. Esse retrato e o de ANTES do movimento, e traz de volta
    // justamente os dois campos que decidem a caixa: `aceleres_personal_box`
    // (ainda `true`) e `additional_attributes.personal_claimed_at` (ainda
    // ausente). Ele nao ficava so no valor de retorno: `SET_ALL_CONVERSATION`
    // SUBSTITUI o registro do store, e o ChatList ainda o repassa para
    // `synchronizeConversationInAceleresCaches`, que o grava em todos os
    // snapshots de caixa (memoria e sessionStorage). Resultado relatado: a
    // conversa some de Meus, continua listada em Pessoais e volta a alternar
    // entre as duas a cada refresh de fundo ou troca de aba.
    const conversation = await dispatch('getConversation', {
      id: conversationId,
      forceRefresh: true,
    });
    await refreshConversationStats(dispatch, state);
    return conversation;
  },

  setCurrentChatAssignee({ commit }, { conversationId, assignee }) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee });
  },

  assignTeam: async ({ dispatch, state }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
      await refreshConversationStats(dispatch, state);
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  // Aceleres — a conclusão em massa não passa por `toggleStatus`.
  //
  // `POST /bulk_actions` só ENFILEIRA (`BulkActionsJob`, fila `medium`) e
  // devolve 200 na hora; quem atualizava a tela era o broadcast que chega
  // quando o job roda. Medido em homologação (24/08/2026): 5,6 s entre o
  // clique em "Concluir conversas" e o `conversation.status_changed` — e se o
  // evento se perder (aba oculta, reconexão de websocket, fila represada) a
  // conversa fica na caixa Meus até um F5. Como a barra de seleção já anuncia
  // sucesso ao usuário, o store passa a refletir o mesmo estado que a tela
  // afirma; um estado divergente é corrigido pelo próprio broadcast ou pelo
  // refresh de segurança da lista.
  applyConversationStatusLocally(
    { commit },
    { conversationIds = [], status, snoozedUntil = null }
  ) {
    if (!status) return;

    conversationIds.forEach(conversationId => {
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status,
        snoozedUntil,
      });
    });
  },

  toggleStatus: async (
    { commit, dispatch, state },
    {
      conversationId,
      status,
      snoozedUntil = null,
      customAttributes = null,
      conclusionResult = null,
    }
  ) => {
    try {
      // Update custom attributes first if provided
      if (customAttributes) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes,
        });
        commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId,
          customAttributes,
        });
      }

      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
        conclusionResult,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
      await refreshConversationStats(dispatch, state);
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    return dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async (
    { commit, state, rootGetters },
    pendingMessage
  ) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      // O blob local do anexo viaja para a mensagem do servidor: a bolha e
      // remontada aqui (a chave muda de uuid para id) e sem ele a midia que o
      // navegador ja tem some do ecra ate acabar de descarregar do servidor.
      const mensagemDoServidor = mergeLocalMediaPreviews(pendingMessage, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(
        types.ADD_MESSAGE,
        sanitizeMessageConversationUnreadForViewer(
          mensagemDoServidor,
          rootGetters?.getCurrentUser?.id,
          {
            preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
              state?.conversationFilters?.aceleresBox
            ),
          }
        )
      );
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const rawError = error.response?.data?.error;
      const errorMessage = typeof rawError === 'string' ? rawError : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, dispatch, state, rootGetters }, message) {
    const sanitizedMessage = sanitizeMessageConversationUnreadForViewer(
      message,
      rootGetters?.getCurrentUserID,
      {
        preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
          state?.conversationFilters?.aceleresBox
        ),
      }
    );
    commit(types.ADD_MESSAGE, sanitizedMessage);
    if (sanitizedMessage.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: sanitizedMessage.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, sanitizedMessage);
    }

    if (
      sanitizedMessage.message_type !== MESSAGE_TYPE.ACTIVITY &&
      !sanitizedMessage.private
    ) {
      scheduleConversationPreviewReconciliation(
        dispatch,
        sanitizedMessage.conversation_id
      );
    }
  },

  // Releitura autoritativa da conversa para corrigir last_non_activity_message
  // quando o broadcast realtime (sem escopo por agente) divergiu do que a API
  // de listagem (com Aceleres::ConversationHistoryScope) consideraria visível.
  reconcileConversationPreview({ commit, dispatch, state }, conversationId) {
    // O jbuilder de conversa decide, a partir de params[:aceleres_box], se o
    // unread_count deve ser computado do ponto de vista do vendedor
    // (supervisores acompanhando Outros/Pessoais) ou do próprio viewer. Sem
    // repassar a caixa ativa aqui, o backend zera o unread_count de conversas
    // atribuídas a outra pessoa, fazendo o card "sumir" como lido mesmo
    // quando o contador da aba continua correto.
    const aceleresBox = state?.conversationFilters?.aceleresBox;
    return ConversationApi.show(conversationId, { aceleresBox })
      .then(({ data }) => {
        if (data?.aceleres_box_match === false) {
          // A conversa aberta precisa continuar no store mesmo quando deixa de
          // pertencer à caixa ativa. Removê-la faz getSelectedChat retornar {}
          // e monta por um instante o placeholder "Selecione uma conversa".
          // Conversas não selecionadas continuam sendo retiradas da lista.
          if (Number(state?.selectedChatId) !== Number(conversationId)) {
            commit(types.DELETE_CONVERSATION, conversationId);
          }
          return;
        }
        const currentConversation = state?.allConversations?.find(
          conversation => Number(conversation?.id) === Number(conversationId)
        );
        const authoritativeMessages = Array.isArray(data?.messages)
          ? data.messages
          : [];
        const previewMessage = data?.last_non_activity_message;
        const previewIsAvailable =
          !previewMessage?.id ||
          [
            ...(currentConversation?.messages || []),
            ...authoritativeMessages,
          ].some(message => String(message?.id) === String(previewMessage.id));

        // A resposta de show traz a mesma janela autoritativa usada para formar
        // last_non_activity_message. Hidrate essa janela antes de atualizar o
        // card: se o evento realtime foi perdido, UPDATE_CONVERSATION preserva
        // de propósito o array local e deixaria a preview avançar sozinha.
        if (authoritativeMessages.length) {
          commit(types.SET_MISSING_MESSAGES, {
            id: data?.id || conversationId,
            data: authoritativeMessages,
          });
        }

        // Se a API ainda não entregou a mensagem dentro da janela de histórico,
        // preserve a preview anterior. Assim o card nunca anuncia uma mensagem
        // que o atendente ainda não consegue ver no chat.
        const reconciledConversation = previewIsAvailable
          ? data
          : {
              ...data,
              last_non_activity_message:
                currentConversation?.last_non_activity_message || null,
            };

        dispatch('updateConversation', reconciledConversation);
        // A cache de sessão do ChatList (aceleresConversationCache) mescla
        // mensagens realtime de forma otimista, fora do Vuex. Avisamos por
        // esse evento para ela também adotar o preview autoritativo, evitando
        // que a versão desatualizada volte ao trocar de aba/reabrir a lista.
        emitter.emit(
          BUS_EVENTS.ACELERES_CONVERSATION_PREVIEW_RECONCILED,
          reconciledConversation
        );
      })
      .catch(() => {
        // Silencioso: é apenas uma correção best-effort da preview; a próxima
        // mensagem ou o próximo reload da lista tentará novamente.
      });
  },

  updateMessage({ commit, state, rootGetters }, message) {
    const sanitizedMessage = sanitizeMessageConversationUnreadForViewer(
      message,
      rootGetters?.getCurrentUserID,
      {
        preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
          state?.conversationFilters?.aceleresBox
        ),
      }
    );
    commit(types.ADD_MESSAGE, sanitizedMessage);
  },

  editMessage: async function editMessage(
    { commit },
    { conversationId, messageId, content }
  ) {
    const { data } = await MessageApi.patchContent(
      conversationId,
      messageId,
      content
    );
    commit(types.ADD_MESSAGE, data);
    return data;
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation(
    { commit, state, dispatch, rootState, rootGetters },
    conversation
  ) {
    const { currentInbox, appliedFilters } = state;
    const sanitizedConversation = sanitizeConversationUnreadForViewer(
      conversation,
      rootGetters?.getCurrentUser?.id,
      {
        preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
          state?.conversationFilters?.aceleresBox
        ),
      }
    );
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = sanitizedConversation;
    const hasAppliedFilters = !!appliedFilters.length;
    // Aceleres 2026-09-15: e-mail não é atendimento. Sem uma inbox aberta, a
    // conversa de e-mail que chega pelo socket não entra na lista — o backend
    // (ConversationFinder#exclude_email_inboxes) já a tira das caixas.
    const isEmailInbox =
      rootGetters?.['inboxes/getInbox']?.(inboxId)?.channel_type ===
      'Channel::Email';
    const isMatchingInboxFilter = currentInbox
      ? Number(currentInbox) === inboxId
      : !isEmailInbox;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnParticipatingView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, sanitizedConversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, state, dispatch, rootGetters }, conversation) {
    const sanitizedConversation = sanitizeConversationUnreadForViewer(
      conversation,
      rootGetters?.getCurrentUser?.id,
      {
        preserveAssignedUnread: shouldPreserveAssignedUnreadForBox(
          state?.conversationFilters?.aceleresBox
        ),
      }
    );
    const {
      meta: { sender },
    } = sanitizedConversation;

    commit(types.UPDATE_CONVERSATION, sanitizedConversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: sanitizedConversation.id,
      data: sanitizedConversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  // Aceleres — e tem de entrar na lista de quem recebeu.
  //
  // A outra face de `evictConversationIfNoLongerMine`: `UPDATE_CONVERSATION` só
  // atualiza a conversa quando ela JÁ está na lista (`if (index > -1)`), então
  // quem passava a ser o responsável só via o card depois de um F5.
  //
  // Delega para `addConversation`, que já traz as guardas do fluxo de
  // `conversation.created` (não age com filtro aplicado, nem nas visões de
  // pastas/menções/participando/não-atendidas, e respeita o filtro de inbox), e
  // cuja mutação é idempotente. A ordenação não é problema: `getAllConversations`
  // reordena a lista a cada leitura, então a posição do push é irrelevante.
  //
  // A condição é tão estreita quanto a da remoção: só quando a conversa é
  // comprovadamente minha E pertence à caixa aberta. `attendance_started` é o
  // que separa Novos de Meus, e vem no próprio payload do evento.
  addConversationIfNowMine({ dispatch, state, rootGetters }, conversation) {
    const box = state?.conversationFilters?.aceleresBox;
    if (box !== 'meus' && box !== 'novos') return;

    const currentUserId = rootGetters?.getCurrentUser?.id;
    if (!currentUserId) return;

    if (conversation?.meta?.assignee?.id !== currentUserId) return;

    const iniciada = !!conversation?.additional_attributes?.attendance_started;
    if (box === 'meus' && !iniciada) return;
    if (box === 'novos' && iniciada) return;

    dispatch('addConversation', conversation);
  },

  // Aceleres — o card tem de sair da lista de quem perdeu a conversa.
  //
  // Reclamação de 21/08/2026: depois de o rodízio transferir, o vendedor
  // anterior continuava vendo a conversa até dar F5. O backend já avisa — o
  // token do responsável anterior entra em `assignee_changed` —, quem não
  // reagia era a lista: `updateConversation` atualiza o registro na store, mas
  // as caixas "Meus" e "Novos" exigem que a conversa seja MINHA, e ninguém
  // reavaliava isso ao receber o evento.
  //
  // Deliberadamente estreito: só remove quando a conversa passou a ter um
  // responsável que NÃO sou eu, e só nas duas caixas que exigem posse.
  // Espelhar aqui a regra inteira das caixas já causou regressão antes (grupos
  // sumindo de Novos), então o que estiver em dúvida fica na lista e o próximo
  // refetch resolve. Grupo nunca tem responsável, então a própria condição
  // abaixo já o mantém fora deste caminho.
  evictConversationIfNoLongerMine(
    { commit, state, rootGetters },
    conversation
  ) {
    const box = state?.conversationFilters?.aceleresBox;
    if (box !== 'meus' && box !== 'novos') return;

    const currentUserId = rootGetters?.getCurrentUser?.id;
    if (!currentUserId) return;

    const assigneeId = conversation?.meta?.assignee?.id;
    if (!assigneeId || assigneeId === currentUserId) return;

    commit(types.DELETE_CONVERSATION, conversation.id);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    const { data } = await ConversationApi.mute(conversationId);
    commit('conversationTypingStatus/clearContact', data.contact_id, { root: true });
    commit(types.MUTE_CONVERSATION, {
      conversationId,
      contactId: data.contact_id,
      additionalAttributes: data.additional_attributes,
    });
    commit(`contacts/${types.SET_CONTACT_ITEM}`, {
      id: data.contact_id,
      blocked: data.blocked,
      ...(data.additional_attributes && {
        additional_attributes: data.additional_attributes,
      }),
    });
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    const { data } = await ConversationApi.unmute(conversationId);
    commit(types.UNMUTE_CONVERSATION, {
      conversationId,
      contactId: data.contact_id,
      additionalAttributes: data.additional_attributes,
    });
    commit(`contacts/${types.SET_CONTACT_ITEM}`, {
      id: data.contact_id,
      blocked: data.blocked,
      ...(data.additional_attributes && {
        additional_attributes: data.additional_attributes,
      }),
    });
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    await ConversationApi.sendEmailTranscript({ conversationId, email });
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  // Aceleres: fixar/desafixar precisa aparecer no clique, nao na resposta do
  // POST — `toggle_pin` ja levou de 64ms a 2,1s em homologacao, e a lista so
  // reordenava quando a resposta chegava. `ChatList` aplica o estado novo com
  // esta action antes de ir na API e usa ela de novo para reverter no erro
  // (inclusive no 422 do limite de 3 fixadas).
  setConversationPin: (
    { commit },
    { conversationId, isPinned, pinnedAt = null }
  ) => {
    commit(types.UPDATE_CONVERSATION_PIN, {
      conversationId: Number(conversationId),
      isPinned: Boolean(isPinned),
      pinnedAt,
    });
  },

  togglePin: async ({ commit }, conversationId) => {
    const { data } = await ConversationApi.togglePin(conversationId);
    commit(types.UPDATE_CONVERSATION_PIN, {
      conversationId: Number(data.id || conversationId),
      isPinned: Boolean(data.is_pinned),
      pinnedAt: data.pinned_at,
    });
    return data;
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  reactToMessage: async (
    { commit },
    {
      accountId,
      conversationId,
      messageId,
      emoji,
      currentUserId,
      currentUserName,
      previousReactions,
    }
  ) => {
    // Update otimista, igual ao menu de contexto: sem ele a chip nova ficava
    // em 0 durante os segundos da ida ate a WAME. A reacao anterior do
    // agente e substituida (uma reacao por conta, como no WhatsApp).
    const canOptimistic =
      Array.isArray(previousReactions) && conversationId && currentUserId;
    const snapshot = canOptimistic
      ? previousReactions.map(reaction => ({
          user_id: reaction.user_id ?? reaction.userId,
          name: reaction.name,
          emoji: reaction.emoji,
          from_contact: reaction.from_contact ?? reaction.fromContact,
          sender_external_id:
            reaction.sender_external_id ?? reaction.senderExternalId,
          ...Object.fromEntries(
            Object.entries(reaction).filter(
              ([key]) =>
                !['userId', 'fromContact', 'senderExternalId'].includes(key)
            )
          ),
        }))
      : null;
    if (canOptimistic) {
      const agentIdentity = `agent:${currentUserId}`;
      const isMine = reaction =>
        !reaction.from_contact &&
        (Number(reaction.user_id) === Number(currentUserId) ||
          reaction.sender_external_id === agentIdentity);
      const previous = snapshot.find(isMine);
      const optimistic = snapshot.filter(reaction => !isMine(reaction));
      if (previous?.emoji !== emoji) {
        optimistic.push({
          user_id: currentUserId,
          name: currentUserName,
          emoji,
          sender_external_id: agentIdentity,
        });
      }
      commit(types.UPDATE_MESSAGE_REACTIONS, {
        conversationId,
        messageId,
        reactions: optimistic,
      });
    }
    try {
      const { data } = await AceleresMessageApi.react(
        accountId,
        messageId,
        emoji
      );
      // eslint-disable-next-line no-console
      console.log('[aceleres-react] response', data);
      commit(types.UPDATE_MESSAGE_REACTIONS, {
        conversationId: data.conversation_id,
        messageId: data.id,
        reactions:
          (data.content_attributes && data.content_attributes.reactions) || [],
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[aceleres-react] failed',
        error?.response?.status,
        error?.response?.data || error
      );
      if (snapshot) {
        commit(types.UPDATE_MESSAGE_REACTIONS, {
          conversationId,
          messageId,
          reactions: snapshot,
        });
      }
      useAlert(
        error?.response?.data?.error ||
          'Não foi possível sincronizar a reação com o WhatsApp.'
      );
    }
  },

  pinMessage: async (
    { commit, getters },
    { conversationId, accountId, messageId, duration }
  ) => {
    try {
      const { data } = await AceleresMessageApi.pin(
        accountId,
        messageId,
        duration
      );
      const conversation = getters.getConversationById(conversationId);
      const existingAttrs = conversation?.additional_attributes || {};
      // Ate quatro fixadas: a lista e a fonte, `pinned_message_id` fica como
      // eco da mais recente para o formato antigo (ver helper/pinnedMessages).
      commit(types.UPDATE_CONVERSATION, {
        id: conversationId,
        additional_attributes: {
          ...existingAttrs,
          pinned_message_ids: data.pinned_message_ids || [],
          pinned_messages: data.pinned_messages || [],
          // Prazo de cada fixada, escolhido no modal. Sem isto o painel nao
          // sabe quando parar de mostrar a fixada (ver helper/pinnedMessages).
          pinned_message_untils: data.pinned_message_untils || {},
          pinned_message_id: data.pinned_message_id || null,
          pinned_message: data.message || null,
        },
      });
      return data;
    } catch (error) {
      // O teto de fixadas volta 422 com a explicacao; sem aviso o clique
      // parece nao ter feito nada.
      const message = error?.response?.data?.error;
      if (message) useAlert(message);
      return Promise.resolve(error);
    }
  },

  starMessage: async ({ commit }, { accountId, messageId }) => {
    try {
      const { data } = await AceleresMessageApi.star(accountId, messageId);
      commit(types.SET_CURRENT_USER_UI_SETTINGS, {
        uiSettings: {
          starred_message_ids: data.starred_message_ids || [],
        },
      });
      return data;
    } catch (error) {
      return Promise.resolve(error);
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
